import React from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import './PortfolioDetails.css';

const PortfolioDetails: React.FC = () => {
  const { state } = usePortfolio();
  const { portfolio } = state;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  if (portfolio.holdings.length === 0) {
    return (
      <div className="portfolio-details">
        <div className="details-header">
          <h3>Your Holdings</h3>
          <span className="details-subtitle">Start trading to see your positions</span>
        </div>
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p>No holdings yet</p>
          <small>Buy some stocks to start building your portfolio</small>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-details">
      <div className="details-header">
        <h3>Your Holdings</h3>
        <span className="details-subtitle">{portfolio.holdings.length} position{portfolio.holdings.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="holdings-table">
        <div className="table-header">
          <div className="col-ticker">Ticker</div>
          <div className="col-quantity">Quantity</div>
          <div className="col-price">Avg Price</div>
          <div className="col-current">Current</div>
          <div className="col-value">Value</div>
          <div className="col-pl">P/L</div>
        </div>

        <div className="table-body">
          {portfolio.holdings.map((holding) => (
            <div key={holding.ticker} className="table-row">
              <div className="col-ticker">
                <div className="ticker-info">
                  <span className="ticker-symbol">{holding.ticker}</span>
                </div>
              </div>
              <div className="col-quantity">
                {holding.quantity.toLocaleString()}
              </div>
              <div className="col-price">
                {formatCurrency(holding.averagePrice)}
              </div>
              <div className="col-current">
                {formatCurrency(holding.currentPrice)}
              </div>
              <div className="col-value">
                {formatCurrency(holding.totalValue)}
              </div>
              <div className={`col-pl ${holding.unrealizedPL >= 0 ? 'positive' : 'negative'}`}>
                <div className="pl-amount">
                  {formatCurrency(holding.unrealizedPL)}
                </div>
                <div className="pl-percent">
                  {formatPercent(holding.unrealizedPLPercent)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="holdings-summary">
        <div className="summary-row">
          <span>Total Invested:</span>
          <span>
            {formatCurrency(
              portfolio.holdings.reduce((sum, h) => sum + (h.quantity * h.averagePrice), 0)
            )}
          </span>
        </div>
        <div className="summary-row">
          <span>Total Value:</span>
          <span>
            {formatCurrency(
              portfolio.holdings.reduce((sum, h) => sum + h.totalValue, 0)
            )}
          </span>
        </div>
        <div className="summary-row total">
          <span>Total P/L:</span>
          <span className={portfolio.holdings.reduce((sum, h) => sum + h.unrealizedPL, 0) >= 0 ? 'positive' : 'negative'}>
            {formatCurrency(
              portfolio.holdings.reduce((sum, h) => sum + h.unrealizedPL, 0)
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetails;
